import { IExcessRequest, IPremiumRequest } from './addon.model';
import { IVehicle } from './vehicles.model';

export type LimitOfLiabilityType =
  | 'DeathInjuryPerPerson'
  | 'DeathInjuryPerEvent'
  | 'PropertyDamage'
  | 'Combined';

export const LimitOfLiabilityTypes: LimitOfLiabilityType[] = [
  'DeathInjuryPerPerson',
  'DeathInjuryPerEvent',
  'PropertyDamage',
  'Combined',
];

export interface IRiskRequest {
  vehicleId: number;
  vehicle?: IVehicle;
  photosUrls: string[];
  perilIds: number[];
  limitsOfLiabilities: ILimitsOfLiabilityRequest[];
  extensions: IRiskExtensionRequest[];
  exclusions?: IRiskExclusionRequest[];
  warranties?: IRiskWarrantyRequest[];
  discounts: IRiskDiscountsRequest[];
  excesses: IExcessRequest[];
  premiumRate: number | null;
  basicPremium: number | null;
  sumInsured: number | null;
  insuredName?: string;
  startDate?: Date;
  endDate?: Date;
  motorBodyTypeId?: number;
}

export interface ILimitsOfLiabilityRequest {
  premium?: IPremiumRequest | null;
  standardLimit: number;
  newLimit: number;
  liabilityType: LimitOfLiabilityType;
}

/**
 * One re-valued liability type in a limits-of-liability endorsement. Only the
 * limit is sent: the affected risks keep the premium rate/amount they already
 * carry, so the uplift stays priced the way it was underwritten.
 */
export interface ILimitOfLiabilityValueUpdate {
  liabilityType: LimitOfLiabilityType;
  newLimit: number;
}

/**
 * Limits-of-liability endorsement. At policy level each entry re-values that
 * liability type on every risk of the policy that carries it; at risk level only
 * on the one risk.
 */
export interface ILimitsOfLiabilityEndorsementRequest {
  limitsOfLiabilities: ILimitOfLiabilityValueUpdate[];
}

export interface IInsurerLimitsOfLiabilityRequest {
  premium: IPremiumRequest;
  liabilityType: LimitOfLiabilityType;
}

export interface IProductLimitsOfLiabilityRequest {
  premium: number;
  liabilityType: LimitOfLiabilityType;
}

export interface IRiskExtensionRequest {
  extensionId: number;
  name: string;
  sumInsured: number;
  premium: IPremiumRequest;
  maxSumInsured?: number;
  minSumInsured?: number;
  maxPremium?: number;
  minPremium?: number;
}

/**
 * Exclusion applied to a risk. A positive premium is a rebate: Rate is a % of
 * sumInsured, or of the basic premium when sumInsured is 0. Null = no rebate.
 */
export interface IRiskExclusionRequest {
  exclusionId: number;
  sumInsured: number;
  premium?: IPremiumRequest | null;
  exclusionName?: string;
}

/**
 * Warranty applied to a risk. A positive premium is a discount: Rate is a % of
 * sumInsured, or of the basic premium when sumInsured is 0. Null = wording only.
 */
export interface IRiskWarrantyRequest {
  warrantyId: number;
  sumInsured: number;
  premium?: IPremiumRequest | null;
  warrantyName?: string;
}

export interface IRiskDiscountsRequest {
  name: string;
  discountId: number;
  type: string;
  sumInsured: number;
  premium: IPremiumRequest;
  maxSumInsured?: number;
  minSumInsured?: number;
  maxPremiumRate?: number;
  minPremiumRate?: number;
}

/**
 * Raw backend response shape for extension data within a risk.
 * Carries snake_case/different field names than the normalized IRiskExtensionRequest.
 */
export interface IBackendExtension {
  extensionName?: string;
  name?: string;
  extensionId?: number;
  id?: number;
  premium?: {
    type?: string;
    value?: number;
    currency?: string;
  };
  amount?: number;
  amountType?: string;
  sumInsured?: number;
}

/**
 * Raw backend response shape for discount data within a risk.
 */
export interface IBackendDiscount {
  discountName?: string;
  name?: string;
  discountId?: number;
  id?: number;
  premium?: {
    type?: string;
    value?: number;
    currency?: string;
  };
  amountType?: string;
  amount?: number;
  sumInsured?: number;
}

/**
 * Raw backend response shape for limit-of-liability data within a risk.
 */
export interface IBackendLimitOfLiability {
  id?: number;
  name?: string;
  liabilityType?: string;
  newLimit?: number;
  amount?: number;
  standardLimit?: number;
  sumInsured?: number;
  premium?: {
    id?: number;
    createdAt?: string | Date;
    updatedAt?: string | Date;
    value?: number;
    currency?: string;
    type?: string;
  };
}
